import { useMutation, useQuery } from '@tanstack/react-query';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { VaultAccountType } from '@/types/myVaultTypes';
import { loadVaultAccountStatus, updateVaultAccountStatus } from '../../api/api';
import { Typography } from '@/components/Typography/Typography';
import { Notification } from '@/components/Notification/Notification';
import { COLORS } from '@/styles/colors';
import { Box } from '@/components/Box/Box';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { DialogButtons } from '@/components-new/Dialog/styles';

type CloseVaultAccountModalType = {
  isOpen: boolean;
  account: VaultAccountType;
  onClose: () => void;
  onSuccess: () => void;
};

export const CloseVaultAccountModal = ({ isOpen, account, onClose, onSuccess }: CloseVaultAccountModalType) => {
  const { data: statusResponse } = useQuery({
    queryKey: ['my_vault_account_status', account.id],
    queryFn: () => loadVaultAccountStatus(account.id),
  });
  const openLinks = statusResponse?.body?.open_links;

  const { mutate, isPending } = useMutation({
    mutationKey: ['close_vault_account'],
    mutationFn: () => updateVaultAccountStatus(account.id, { status: 'closed' }),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CloseVaultAccountSuccess);
      showSnackbar(`${account.nickname} closed. ${response.body.links_revoked ?? 0} links revoked.`);
      onSuccess();
      onClose();
    },
  });

  return (
    <Dialog isOpen={isOpen} header='Close account' onClose={onClose}>
      <Box display='flex' gap='14px' flexDirection='column' textAlign='left'>
        <Typography color={COLORS.black}>
          Move{' '}
          <b>
            {account.nickname} ({account.bank_name} {account.masked_number})
          </b>{' '}
          from {account.vault} to Closed accounts?
        </Typography>
        <Notification variant='warning'>
          {openLinks == null
            ? 'Checking open links...'
            : openLinks
              ? `${openLinks} open link${openLinks === 1 ? '' : 's'} for this account will be revoked now.`
              : 'This account has no open links.'}
        </Notification>
        <Typography variant='bodySmall'>
          You can reopen it later from Closed accounts. Revoked links stay revoked.
        </Typography>

        <DialogButtons>
          <ButtonNew variant='secondary' onClick={onClose}>
            Cancel
          </ButtonNew>
          <ButtonNew variant='danger' onClick={() => mutate()} isLoading={isPending}>
            Close account
          </ButtonNew>
        </DialogButtons>
      </Box>
    </Dialog>
  );
};
